// ── Stitch client (browser side) ──
//
// All Stitch traffic goes through our OWN backend (/api/stitch/*), exactly like the
// Anthropic extraction proxy — the Stitch client PRIVATE KEY (which signs the
// client_assertion) never enters the browser bundle. Every function here is GUARDED:
// while the feature is dormant (no flag / no client id) it throws BankFeedDisabledError
// instead of calling anything.
//
// Stitch financial-data flow (OAuth 2.0 + OpenID Connect, PKCE):
//   1. openConnectWidget() → redirect the user to Stitch's authorize URL (with a PKCE
//      challenge + our scopes). The user picks their bank and consents. Stitch redirects
//      back to STITCH_REDIRECT_URI with an authorization `code`.
//   2. exchangeCode(code, codeVerifier) → server swaps the code (signing a
//      client_assertion JWT with the private key) for access + refresh tokens, persists
//      the refresh token server-side, and returns an opaque linkId.
//   3. confirmDataReady(linkId) → Stitch data is queryable immediately after linking,
//      so this resolves 'AVAILABLE' (kept for interface parity with the upload path).
//   4. fetchTransactions(linkId, ...) → server runs the GraphQL query and returns nodes.
//   5. fetchReauthCode(linkId) → refresh the token; signals when a re-link is required.
//
// The function NAMES intentionally match the previous Mono client so the surrounding
// pipeline (categorise → review → post) is untouched.

import { authHeader } from "../firebase";
import {
  isBankFeedEnabled,
  BankFeedDisabledError,
  STITCH_CLIENT_ID,
  STITCH_REDIRECT_URI,
  STITCH_SCOPES,
} from "./config";

const API_BASE = process.env.REACT_APP_ENGINE_URL ?? "http://localhost:3001";
const STITCH_BASE = API_BASE + "/api/stitch";

// Stitch hosted authorization endpoint (browser is redirected here to link a bank).
const STITCH_AUTHORIZE_URL = "https://secure.stitch.money/connect/authorize";

async function postStitch(path, body) {
  if (!isBankFeedEnabled()) throw new BankFeedDisabledError();
  const res = await fetch(STITCH_BASE + path, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await authHeader()) },
    body: JSON.stringify(body || {}),
  });
  if (res.status === 503) {
    // Server has no STITCH_CLIENT_KEY — the feature is not provisioned yet.
    throw new BankFeedDisabledError("The bank feed is not configured on the server yet.");
  }
  if (!res.ok) {
    const err = await res.text();
    throw new Error("Stitch API " + res.status + ": " + err.slice(0, 400));
  }
  return res.json();
}

// ── PKCE helpers (browser crypto, no dependency) ──
function base64UrlFromBytes(bytes) {
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/=/g, "").replace(/\+/g, "-").replace(/\//g, "_");
}

function randomVerifier() {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64UrlFromBytes(bytes);
}

async function challengeFromVerifier(verifier) {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64UrlFromBytes(new Uint8Array(digest));
}

// Exchange the authorization `code` for tokens. `codeVerifier` is the PKCE verifier we
// stashed before redirecting (see openConnectWidget). Returns an opaque linkId the
// server maps to the persisted refresh token.
export async function exchangeCode(code, codeVerifier) {
  if (!code) throw new Error("No Stitch authorization code provided");
  const data = await postStitch("/exchange", {
    code,
    codeVerifier: codeVerifier || sessionStorage.getItem("stitch_pkce_verifier") || "",
    redirectUri: STITCH_REDIRECT_URI,
  });
  sessionStorage.removeItem("stitch_pkce_verifier");
  return data.linkId || data.id; // opaque server-side link reference
}

// Stitch financial data is queryable as soon as the account is linked — there's no
// Mono-style "data_status" gate. Kept for interface parity with the upload pipeline.
export async function confirmDataReady(/* linkId */) {
  return "AVAILABLE";
}

// Pull transactions for a linked account. `opts` may carry { first, after } for
// GraphQL cursor pagination. Returns the array of transaction nodes.
export async function fetchTransactions(linkId, opts) {
  const data = await postStitch("/transactions", { linkId, ...(opts || {}) });
  return data.transactions || data.data || [];
}

// Refresh the access token for a link. Returns null when the refresh succeeds, or a
// truthy re-link signal when Stitch requires the user to re-authorise (consent expired
// / revoked). Mirrors the old fetchReauthCode contract.
export async function fetchReauthCode(linkId) {
  const data = await postStitch("/reauth", { linkId });
  return data.relinkRequired ? (data.authorizeUrl || true) : null;
}

// Begin bank linking: build the Stitch authorize URL with a fresh PKCE challenge and
// redirect the browser to Stitch's hosted consent flow. Dormant-safe: refuses before
// doing anything if the feature isn't enabled. Resolves after navigation is triggered
// (the `code` comes back on STITCH_REDIRECT_URI, handled by the callback route).
export async function openConnectWidget({ state } = {}) {
  if (!isBankFeedEnabled()) return Promise.reject(new BankFeedDisabledError());
  const verifier = randomVerifier();
  const challenge = await challengeFromVerifier(verifier);
  sessionStorage.setItem("stitch_pkce_verifier", verifier);
  const params = new URLSearchParams({
    client_id: STITCH_CLIENT_ID,
    scope: STITCH_SCOPES.join(" "),
    response_type: "code",
    redirect_uri: STITCH_REDIRECT_URI,
    state: state || randomVerifier(),
    nonce: randomVerifier(),
    code_challenge: challenge,
    code_challenge_method: "S256",
  });
  window.location.assign(STITCH_AUTHORIZE_URL + "?" + params.toString());
  return Promise.resolve();
}
